<template>
  <Container :class="$style.container">
    <ButtonBack
      text="Dashboard"
      :to="$options.homeRoute"
    />
    <Container :class="$style.innerContainer">
      <HeadingMedium :class="$style.headerStyle">
        {{ headerText }}
      </HeadingMedium>
      <ItemListPagination
        :class="$style.list"
        :columns="tableColumns"
        :currentPageNum="currentPageNum"
        :isLoading="loading"
        :itemComponent="$options.DeviceRow"
        :rowsPerPage="rowsPerPage"
        :showEmptyState="!!getSearchTerm && !getCollection.length"
        :sortBy="sortBy"
        :sortDirection="sortDirection"
        :tableData="getCollection"
        :totalCount="totalCount"
        @pageChange="handlePageChange"
        @sortColumn="handleSortColumn"
      >
        <template v-slot:toolbar>
          <HomeSearchBar
            :count="totalCount"
            :disabled="false"
            searchPlaceholder="Search"
            :shouldFocusSearch="shouldFocusSearch"
            :toolBarDisplayName="headerText"
            @triggerSearch="handleEnterSearchTerm"
          />
        </template>
      </ItemListPagination>
      <Container :class="$style.buttonContainer">
        <Button
          :text="buttonText"
          textCase="capitalize"
          type="primary"
          @click="handleRedirect($options.deviceList)"
        />
      </Container>
    </Container>
  </Container>
</template>

<script>
import {
  Button,
  ButtonBack,
  Container,
  HeadingMedium,
  ItemListPagination,
  Types,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import DeviceRow from 'client/HomePageV2/components/DeviceRow.vue';
import HomeSearchBar from 'client/HomePageV2/components/HomeSearchBar.vue';
import HomeWidgetConsts from 'client/HomePageV2/constants/HomeWidgetConsts';
import SegmentEventMixin from 'client/HomePageV2/mixins/SegmentEventMixin';
import SystemApi from 'client/api/v2/SystemApi';
import SystemFilters from 'components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import Util from 'client/util/Util';

const { system } = ItemTypeConsts;
const { Column } = Types;
const { widgetTableColumns } = HomeWidgetConsts;
const systemCollection = StoreModuleMap[system];
const deviceList = ItemTypeRoutes[ItemTypeConsts.deviceList];
const buttonText = 'Take me to Devices Page';
const defaultRowsPerPage = 10;
const defaultSortBy = 'lastContact';
const headerText = 'Devices inactive greater than 7 days';
const filters = [
  SystemFilters.status(false),
  SystemFilters.inactiveDays(7),
];

export default {
  name: 'DeviceInvestigativePages',

  components: {
    Button,
    ButtonBack,
    Container,
    HeadingMedium,
    HomeSearchBar,
    ItemListPagination,
  },

  deviceList,
  DeviceRow,
  homeRoute: ItemTypeRoutes[ItemTypeConsts.home],

  mixins: [SegmentEventMixin],

  data: () => ({
    buttonText,
    currentPageNum: 0,
    headerText,
    filters,
    loading: false,
    rowsPerPage: defaultRowsPerPage,
    shouldFocusSearch: false,
    noResults: false,
    numDevicesInactive: 0,
  }),

  computed: {
    ...mapGetters(systemCollection, [
      'getCollection',
      'getSortField',
      'getSearchTerm',
    ]),

    ...mapState(systemCollection, {
      totalCount: (state) => state.entities?.items.totalCountOfAllItems,
      sort: (state) => state.features.items.sort,
    }),

    sortBy() {
      return this.getSortField.replace('-', '');
    },

    sortDirection() {
      return this.getSortField.charAt(0) === '-' ? 'descending' : 'ascending';
    },

    tableColumns() {
      return widgetTableColumns.deviceNotificationInvestigation.map(column => Column(column));
    },
  },

  created() {
    this.handleCreated();
  },

  beforeDestroy() {
    this.resetCollection();
  },

  methods: {
    ...mapActions(systemCollection, [
      'fetchCollection',
      'resetCollection',
      'setPage',
      'setSearchTerm',
      'setSortField',
      'setFilters',
    ]),

    fetchDevicesInactive() {
      // Get number of devices inactive for over 7 days
      return SystemApi.fetch(0, 1, null, null, null, this.filters)
        .then(({ totalCount }) => {
          if (typeof totalCount === 'number') {
            this.numDevicesInactive = totalCount;
          }
        });
    },

    async handleCreated() {
      await this.fetchDevicesInactive();
      if (this.numDevicesInactive !== 0) {
        await this.reset();
      }
    },

    async handleEnterSearchTerm(searchTerm) {
      // searchTerm with length 0 is valid case we need to handle
      // skip searchTerms that are only spaces though
      if (searchTerm.length && !searchTerm.trim().length) return;
      this.resetCollection();
      this.loading = true;
      this.setFilters({ filtersArray: this.filters });
      this.setSearchTerm({ searchTerm });
      this.shouldFocusSearch = false;
      this.currentPageNum = 0;
      this.updatePageNumber();
      await this.fetchCollection();
      this.loading = false;
    },

    async handlePageChange(payload = {}) {
      const {
        currentPageNum = this.currentPageNum,
        rowsPerPage = this.rowsPerPage,
      } = payload;
      const searchTerm = this.getSearchTerm;
      this.loading = true;
      this.currentPageNum = currentPageNum;
      this.rowsPerPage = rowsPerPage;
      this.setFilters({ filtersArray: this.filters });
      this.setSearchTerm({ searchTerm });
      this.updatePageNumber();
      await this.fetchCollection();
      this.loading = false;
    },

    handleRedirect(url) {
      this.triggerButtonClickedHandler(
        'Device investigative page',
        'Investigation Page',
        'Device investigation',
        url,
        buttonText,
      );
      Util.redirect(url);
    },

    async handleSortColumn(payload = {}) {
      const {
        dataFieldName,
        sortDirection,
      } = payload;
      const sortField = `${sortDirection === 'descending' ? '-' : ''}${dataFieldName}`;
      this.loading = true;
      this.setSortField({ sortField });
      this.updatePageNumber();
      await this.fetchCollection();
      this.loading = false;
    },

    async reset() {
      this.resetCollection();
      this.setFilters({ filtersArray: this.filters });
      if (defaultSortBy !== this.sortBy) {
        this.setSortField({ sortField: defaultSortBy });
      }
      this.updatePageNumber();
      await this.fetchCollection();
    },

    updatePageNumber() {
      const pageNumber = this.currentPageNum;
      this.setPage({ pageNumber, limit: this.rowsPerPage });
    },
  },
};
</script>

<style module scoped>
.buttonContainer {
  display: flex;
  justify-content: flex-end;
}

.container {
  background-color: var(--jc-color-disabled);
  display: inherit;
  height: 100%;
  padding: var(--jc-spacer-x-small);
}

.innerContainer {
  background-color: var(--jc-color-surface-light);
  margin-top: var(--jc-spacer);
}

.headerStyle {
  padding-bottom: var(--jc-spacer-medium);
  padding-top: var(--jc-spacer-medium);
}

.list {
  padding-left: var(--jc-spacer);
}
</style>
